import type {
    EditorContext,
    SaveFileResult,
    EditorActorRef,
} from '@pyranger/types';

import { saveFile, updateFileBrowser } from './fileBrowser';
import { isFileUnsaved } from './textEditor';
import { setIsExecutingCommand } from './repl';
import { waitForMutexRelease } from './deviceMutex';

export const handleSaveFileClick = async ({
    actorRef,
    autoSave = false,
    context,
    promptForPath,
    skipExecutingCheck = false,
}: {
    actorRef: EditorActorRef;
    autoSave?: boolean;
    context: EditorContext;
    /** Async callback to prompt the user for a file path when saving a new file.
     *  Receives the current placeholder path as a default value.
     *  Return the chosen path, or null to cancel the save. */
    promptForPath?: (defaultPath: string) => Promise<string | null>;
    skipExecutingCheck?: boolean;
}): Promise<SaveFileResult> => {
    if (!skipExecutingCheck && context.ref.current.repl.isExecutingCommand) {
        throw new Error('Cannot save file while another command is executing');
    }

    // If a file is currently running, stop execution and wait for cleanup.
    // runCurrentFile's finally block sends Ctrl-B to exit raw REPL mode,
    // which is necessary for the device to be in a known state for the
    // subsequent MpRawMode.begin() call during save.
    if (context.ref.current.repl.isFileRunning) {
        const { stopExecution } = await import('./codeExecution');
        await stopExecution({ context });

        // Wait for runCurrentFile's finally block to release the mutex.
        const released = await waitForMutexRelease(context.ref.current.device);

        // If it still hasn't released, force-reset the device to a known state
        if (!released) {
            // Send Ctrl-B (exit raw REPL) + Ctrl-C (interrupt) to get back to >>> prompt
            const port = context.ref.current.connection.port;
            if (port) {
                const writer = context.ref.current.connection.globalWriter;
                if (writer) {
                    const encoder = new TextEncoder();
                    await writer.write(encoder.encode('\x02')); // Ctrl-B: exit raw REPL
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    await writer.write(encoder.encode('\x03\x03')); // Ctrl-C x2: interrupt
                    await new Promise((resolve) => setTimeout(resolve, 200));
                }
            }
        }
    }

    // Only set the flag if we're not already in an executing command context
    const shouldSetExecuting = !skipExecutingCheck;
    if (shouldSetExecuting) {
        setIsExecutingCommand({
            actorRef,
            context,
            isExecutingCommand: true,
        });
    }

    try {
        const {
            textEditor: { openFiles, currentPath },
        } = context;

        if (currentPath) {
            const content = openFiles.get(currentPath)?.content ?? '';

            if (currentPath.includes('new_file')) {
                // It's a new file that needs a proper path.
                // Use the provided async prompt, or fall back to a basic
                // synchronous prompt (for backward compatibility in tests).
                const resolveNewPath = promptForPath
                    ? promptForPath
                    : (defaultPath: string) =>
                          Promise.resolve(
                              prompt('Enter a name for the file:', defaultPath),
                          );

                const newPath = await resolveNewPath(currentPath);
                if (newPath) {
                    try {
                        await saveFile({
                            autoSave,
                            path: newPath,
                            context,
                            content,
                        });

                        updateFileBrowser({ context });
                        return {
                            renamedFile: {
                                oldPath: currentPath,
                                newPath,
                            },
                        };
                    } catch (error) {
                        console.error('Error saving file:', error);
                        throw error;
                    }
                }
            } else {
                if (isFileUnsaved(currentPath, openFiles)) {
                    try {
                        await saveFile({
                            autoSave,
                            context,
                            path: currentPath,
                            content,
                        });

                        updateFileBrowser({ context });
                        return {
                            savedPath: currentPath,
                        };
                    } catch (error) {
                        console.error('Error saving file:', error);
                        throw error;
                    }
                } else {
                    if (!autoSave) {
                        context.showCustomAlert({
                            title: 'File not saved',
                            message: 'There were no changes to be saved.',
                            variant: 'warning',
                        });
                    }
                }
            }
        } else {
            if (!autoSave) {
                context.showCustomAlert({
                    title: 'Save Error',
                    message: 'No file is currently open for saving.',
                    variant: 'error',
                });
            }
        }
        return {};
    } finally {
        // Only clear the flag if we set it
        if (shouldSetExecuting) {
            setIsExecutingCommand({
                actorRef,
                context,
                isExecutingCommand: false,
            });
        }
    }
};
